/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import { login } from '@/features/auth/services/authService'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { HTTPInstance } from '@/lib/http/http-instance'
import { z } from 'zod'
import { LoginErrorFormEffect } from './LoginErrorFormEffect'

type Restaurant = { id: string | number; [k: string]: any }

export const useLoginFormConfig = () => {
  const { showSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [failureCount, setFailureCount] = useState(0)

  const handleLogin = async (email: string, password: string) => {
    try {
      await login(email, password)

      const { data } = await HTTPInstance.get<Restaurant[]>('/restaurants')
      const restaurants = data ?? []

      if (restaurants.length > 0) {
        const { id, currency } = restaurants[1]
        localStorage.setItem('restaurantId', String(id))
        // Amounts are stored and sent in this venue's minor units, and how
        // many make a major one varies by currency, see shared/utils/price.
        localStorage.setItem('currency', String(currency ?? 'PLN'))
        navigate('/table-cards-management')
      } else {
        throw new Error('No restaurants found for this user.')
      }
    } catch (error: any) {
      console.error('Login failed:', error)
      showSnackbar?.(t('AUTHORIZATION.LOGIN.ERROR') || 'Login failed', 'error')
      setFailureCount(count => count + 1)
    }
  }

  const formConfig = {
    id: 'login-form',
    fields: [
      {
        name: 'email',
        label: t('AUTHORIZATION.LOGIN.EMAIL.LABEL'),
        placeholder: t('AUTHORIZATION.LOGIN.EMAIL.PLACEHOLDER'),
        focused: true,
        type: 'text',
        required: true,
        validation: z
          .string({
            required_error: t('AUTHORIZATION.LOGIN.EMAIL.VALIDATION.REQUIRED')
          })
          .trim()
          .email({
            message: t('AUTHORIZATION.LOGIN.EMAIL.VALIDATION.INVALID')
          })
      },
      {
        name: 'password',
        label: t('AUTHORIZATION.LOGIN.PASSWORD.LABEL'),
        placeholder: t('AUTHORIZATION.LOGIN.PASSWORD.PLACEHOLDER'),
        focused: true,
        type: 'password',
        required: true,
        validation: z
          .string({
            required_error: t(
              'AUTHORIZATION.LOGIN.PASSWORD.VALIDATION.REQUIRED'
            )
          })
          .trim()
          .min(8, {
            message: t('AUTHORIZATION.LOGIN.PASSWORD.VALIDATION.TOO_SHORT')
          })
      }
    ],
    onSubmit: async (data: Record<string, any>) => {
      await handleLogin(data.email, data.password)
    },
    FormEffects: <LoginErrorFormEffect failureCount={failureCount} />
  }

  return formConfig
}
